import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useModal,
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import messages from './UpdateTheftResolutionPopup_AX.messages';
import metadata from './UpdateTheftResolutionPopup_AX.metadata.json5';

function UpdateTheftResolutionPopup(props) {
    const [theftResolutionVM, setTheftResolutionVM] = useState({});
    const [availableValues, setAvailableValues] = useState([]);
    const translator = useContext(TranslatorContext);
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate } = useValidation('UpdateTheftResolutionPopup');
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestNumber,
        authHeader,
        viewModelService
    } = props;
    const {
        showAlert
    } = useModal();
    useEffect(() => {
        const theftResolutions = [];
        viewModelService.productMetadata.get('cc').types.getTypelist('AX_TheftResolution').codes
            .forEach((typecode) => {
                theftResolutions.push(_.merge({}, typecode, {
                    id: typecode.code,
                    displayName: translator({
                        id: `typekey.AX_TheftResolution.${typecode.code}`,
                        defaultMessage: 'Not found'
                    })
                }));
            });
        setAvailableValues(theftResolutions);
        setTheftResolutionVM(
            viewModelService.create(
                {
                    serviceRequestNumber: serviceRequestNumber
                },
                'cc',
                'edge.capabilities.servicerequest.dto.ServiceRequestActionDTO'
            )
        );
    // only execute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSend = useCallback(async () => {
        const updateTheftResolution = await ServiceRequestService.updateTheftResolution(
            theftResolutionVM.value,
            authHeader
        ).catch(() => {
            showAlert({
                title: messages.error,
                message: messages.errorMessage,
                status: 'error',
                icon: 'mi-error-outline'
            });
        });
        onResolve(updateTheftResolution);
    }, [theftResolutionVM, authHeader, onResolve]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    const handleTheftResolution = useCallback((option) => {
        _.set(theftResolutionVM, 'description', option);
        refreshData();
    }, [theftResolutionVM, refreshData]);

    const handleTheftResolutionVMChange = useCallback((newModel) => {
        setTheftResolutionVM(newModel);
        refreshData();
    }, [refreshData]);

    if (_.isEmpty(theftResolutionVM)) {
        return null;
    }

    const overrides = {
        axUpdateTheftResolutionDropdownSelect: {
            availableValues: availableValues
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
            handleTheftResolution: handleTheftResolution
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose}>
            <ModalHeader title={translator(messages.UpdateTheftResolutionTitle)} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={theftResolutionVM}
                    onValidationChange={onValidate}
                    onModelChange={handleTheftResolutionVMChange}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="outlined"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleSend}
                    disabled={!isComponentValid}
                >
                    {translator(messages.send)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

UpdateTheftResolutionPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onResolve: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onAfterClose: PropTypes.func.isRequired,
    serviceRequestNumber: PropTypes.string.isRequired,
    authHeader: PropTypes.string,
    theftResolutionVM: PropTypes.shape({
        serviceRequestNumber: PropTypes.string
    })
};

UpdateTheftResolutionPopup.defaultProps = {
    theftResolutionVM: undefined,
    authHeader: undefined
};

export default UpdateTheftResolutionPopup;
